// src/components/AuthGate.tsx
// 認証必須ページの wrapper: /api/auth/me を叩いて 401 なら /login へ

import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { authApi } from "../lib/auth-api";

interface Props {
  children: React.ReactNode;
  adminOnly?: boolean;
  /** 開発版 (/beta = リリース前タブ): user.beta || is_admin のみ。
   *  ★DB 列は歴史的に users.beta のまま (2026-08-24 に呼称だけ「ベータ版」→「開発版」に統一)。 */
  betaOnly?: boolean;
}

export function AuthGate({ children, adminOnly = false, betaOnly = false }: Props) {
  const loc = useLocation();
  const { data, isLoading, error } = useQuery({
    queryKey: ["auth", "me"],
    queryFn: authApi.me,
    retry: false,
    staleTime: 30_000,
  });

  // 401 等のエラーは fetch 側で window.location 飛ばしてるので、 ここでは早期 return しない
  useEffect(() => {
    if (error) console.log("[AuthGate] auth error:", error.message);
  }, [error]);

  if (isLoading) {
    return (
      <div className="p-8">
        <div className="animate-pulse text-slate-400">認証確認中…</div>
      </div>
    );
  }
  if (error || !data) {
    return <Navigate to={`/login?next=${encodeURIComponent(loc.pathname)}`} replace />;
  }
  if (adminOnly && !data.user.is_admin) {
    return (
      <div className="p-8 max-w-2xl">
        <h1 className="text-xl font-semibold text-red-700 mb-2">管理者専用</h1>
        <p className="text-sm text-slate-600">このページは管理者だけがアクセスできます。</p>
      </div>
    );
  }
  if (betaOnly && !data.user.beta && !data.user.is_admin) {
    return (
      <div className="p-8 max-w-2xl">
        <h1 className="text-xl font-semibold text-amber-700 mb-2">🧪 開発版（権限が必要）</h1>
        <p className="text-sm text-slate-600">このページは開発版です。閲覧には管理者からの利用許可が必要です。</p>
      </div>
    );
  }
  return <>{children}</>;
}
